import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CloseButton } from 'shared/ui';
import { MessageEvent, StatusEvent } from 'pubnub';
import { ECGPackageType } from 'features/vital';
import { addECGPackage, resetECG } from 'pages/session-room/store/ecg';
import { ConnectionStatus } from '../connection-status';
import { useSessionRoomDispatch, useSessionRoomState } from '../../context';
import { ConnectionStatusEnum } from '../connection-status/model';
import {
  VitalsBlock,
  Header,
  HeaderLeft,
  HeaderRight,
  HeaderIcon,
  HeaderTitle,
  Body,
  SensorBlocks,
} from './styles';
import {
  VitalTemperature,
  VitalGlucose,
  VitalSysDia,
  VitalSp02,
  VitalEKG,
  VitalManualWeight,
  VitalManualHeight,
  VitalManualNotes,
} from './ui';
import { VitalMessage } from './model';
import { fetchVitalsHistory, generateVitalObject } from './utils';
import '../../store/init';

export const VitalsWrapper: FC = () => {
  const { t } = useTranslation(['session-room']);
  const { sessionPubNub } = useSessionRoomState();
  const dispatch = useSessionRoomDispatch();
  const [connectionStatus, setConnectionStatus] =
    useState<ConnectionStatusEnum>(ConnectionStatusEnum.PENDING);
  const hydrateFromHistory = useCallback(async () => {
    if (!sessionPubNub) {
      return;
    }

    const merged = await fetchVitalsHistory(
      sessionPubNub.instance,
      sessionPubNub.channels
    );

    if (Object.keys(merged).length > 0) {
      dispatch({
        type: 'SET_VITALS_DATA',
        payload: merged,
      });
    }
  }, [dispatch, sessionPubNub]);

  const handleStatusEvent = useCallback(
    (statusEvent: StatusEvent) => {
      if (statusEvent.category === 'PNConnectedCategory') {
        setConnectionStatus(ConnectionStatusEnum.CONNECTED);
        hydrateFromHistory().catch(() => undefined);
        return;
      }

      setConnectionStatus(ConnectionStatusEnum.DISCONNECTED);
    },
    [hydrateFromHistory]
  );

  const handleMessage = useCallback(
    (message: VitalMessage) => {
      if (message.typeString === 'ECG') {
        addECGPackage(message.data as ECGPackageType);
        return;
      }

      dispatch({
        type: 'SET_VITALS_DATA',
        payload: generateVitalObject(message),
      });
    },
    [dispatch]
  );

  const handleMessageEvent = useCallback(
    ({ message }: MessageEvent) => {
      handleMessage({
        data: message.data,
        typeString: message.type_string,
        timestamp: message.timestamp,
      });
    },
    [handleMessage]
  );

  useEffect(() => {
    if (sessionPubNub) {
      hydrateFromHistory().catch(() => undefined);
    }
  }, [hydrateFromHistory, sessionPubNub]);

  useEffect(() => {
    if (sessionPubNub) {
      sessionPubNub.instance.addListener({
        message: handleMessageEvent,
        status: handleStatusEvent,
      });
      sessionPubNub.instance.subscribe({
        channels: sessionPubNub.channels,
      });

      return () => {
        sessionPubNub.instance.removeListener({
          message: handleMessageEvent,
          status: handleStatusEvent,
        });

        sessionPubNub.instance.unsubscribe({
          channels: sessionPubNub.channels,
        });
      };
    }

    return undefined;
  }, [handleMessage, handleMessageEvent, handleStatusEvent, sessionPubNub]);

  const handleClose = useCallback(() => {
    dispatch({
      type: 'TOGGLE_SHOW_VITALS',
      payload: {
        isShow: false,
      },
    });
  }, [dispatch]);

  useEffect(() => resetECG, []);

  return (
    <VitalsBlock>
      <Header>
        <HeaderLeft>
          <HeaderIcon />
          <HeaderTitle>{t('session-room:VITALS')}</HeaderTitle>
        </HeaderLeft>

        <HeaderRight>
          <ConnectionStatus status={connectionStatus} />
          <CloseButton onClick={handleClose} />
        </HeaderRight>
      </Header>

      <Body>
        <SensorBlocks>
          <VitalSysDia />
          <VitalTemperature />
          <VitalSp02 />
          <VitalGlucose />
          <VitalManualWeight />
          <VitalManualHeight />
        </SensorBlocks>

        <VitalManualNotes />

        <VitalEKG />
      </Body>
    </VitalsBlock>
  );
};
